'use strict';

/**
 * Base class for a CompositeUploadView
 *
 * @category   View
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var ColidoCompositeView = require('./Colido.CompositeView');
var RestrictionImagesModalView = require('modules/base/views/layout/restriction-images-modal');
var RestrictionSpaceModalView = require('modules/base/views/layout/restriction-space-modal');

var CompositeUploadView = ColidoCompositeView.extend({
    /**
     * Settings for the upload
     * @type {Object}
     */
    settings: {
        /**
         * Current count of images
         */
        currentFiles: 0,

        /**
         * Maximum number of uploadable File
         */
        maxFiles: Number.MAX_VALUE,

        /**
         * Maximum number of uploadable File in one upload-selection
         */
        maxAtOnceFiles: 99,

        /**
         * Maximum number of uploadable Bytes altogether
         */
        spaceLeft: Number.MAX_VALUE, // in Bytes

        /**
         * Allowed file types
         */
        // allow: ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'],  //@todo move this into image upload view subl
        allow: [],
    },

    /**
     * Events for Upload initialized?
     * @type {boolean}
     */
    eventsInitialized: false,

    /**
     * Reference to the the hidden file input
     * @type {object}
     */
    hiddenInput: null,

    /**
     * Reference to the the hidden file input for taking a picture
     * @type {object}
     */
    hiddenInputSingle: null,

    /**
     * Reference for created filereader
     */
    fileReader: [],

    //@todo move to initilize
    constructor: function(options) {
        ColidoCompositeView.prototype.constructor.apply(this, arguments);

        this.settings = _.clone(this.settings);

        this.settings.uploadHandler = App.getApiUrl('/assets');

        // Apply settings
        this.settings = _.extend(this.settings, this.getOption('settings'));

        this.listenTo(this, 'before:destroy', this.destroyUploadEventHandlers.bind(this));

        // Listen to model trigger
        this.listenTo(this.collection, 'upload:started', function(model, formData) {
            this.triggerMethod('upload:started', model, formData);
        }.bind(this));

        this.listenTo(this.collection, 'upload:progress', function(model, event) {
            this.triggerMethod('upload:progress', model, event);
        }.bind(this));

        this.listenTo(this.collection, 'upload:completed', function(model, event) {
            this.triggerMethod('upload:completed', model, event);
        }.bind(this));

        this.listenTo(this.collection, 'upload:error', function(model, event) {
            this.triggerMethod('upload:error', model, event);
        }.bind(this));

        this.listenTo(this.collection, 'upload:abort', function(model, event) {
            this.triggerMethod('upload:abort', model, event);
        }.bind(this));

        // Extend the default model
        this.collection.model = this.collection.model.extend({
            /**
             * Upload Handler Script
             * @type {String}
             */
            uploadUrl: this.settings.uploadHandler,

            assetType: this.options.field.type,

            /**
             * XHR object
             * @type {XMLHttpRequest}
             */
            xhr: null,

            /**
             * Upload function for the image
             * @type {function}
             */
            upload: function() {
                var formData = new FormData();
                formData.append('file', this.get('imageFile'));

                // Additional information
                formData.append('fileIndex', this.get('imageIndex'));
                formData.append('type', this.assetType);

                // Processing XHR
                this.xhr = new XMLHttpRequest();
                this.xhr.open('POST', this.uploadUrl, true);
                this.xhr.setRequestHeader('colido-apitoken', App.user.get('apiToken'));

                // Bind proper listeners
                this._bindListener();

                // Send XHR request
                this.xhr.send(formData);

                this.trigger('upload:started', this, formData);
            },

            importFromUrl: function() {
                var formData = {
                    fileUrl: this.get('fileUrl'),
                    fileName: this.get('fileName'),
                    type: 'link',
                };

                // Processing XHR
                this.xhr = new XMLHttpRequest();
                this.xhr.open('POST', this.uploadUrl, true);
                this.xhr.setRequestHeader('colido-apitoken', App.user.get('apiToken'));
                this.xhr.setRequestHeader('Content-Type', 'application/json');

                // Bind proper listeners
                this._bindListener();

                // Send XHR request
                this.xhr.send(JSON.stringify(formData));

                this.trigger('upload:started', this, formData);
            },

            /**
             * Is triggered on upload progress
             * @param {XMLHttpRequestProgressEvent} event
             */
            onUploadProgress: function(event) {
                var percentCompleted = 0;

                if (event.lengthComputable) {
                    percentCompleted = (event.loaded / event.total) * 100;
                    this.set({percentCompleted: percentCompleted});

                    if (percentCompleted === 100) {
                        this.set({
                            uploading: false,
                        });
                    }
                }

                this.trigger('upload:progress', this, event);
            },

            /**
             * Triggered when download is finished
             * @param {XMLHttpRequestProgressEvent} event
             */
            onUploadFinished: function(event) {
                var resp = null;
                if (event.currentTarget.status === 200) {
                    try {
                        resp = JSON.parse(event.currentTarget.response);
                    } catch(exception) { /* Something went wrong */ }
                }

                // Set all informations to the model
                if (resp) {
                    this.set(resp);
                }

                // State, that the image is not uploading anymore
                this.set({
                    importing: false,
                    uploading: false,
                    percentCompleted: 100,
                });

                this.trigger('upload:completed', this, event);
                this._destroyListener();
            },

            /**
             * Triggered when upload failed
             * @param {XMLHttpRequestProgressEvent} event
             */
            onUploadError: function(event) {
                this.trigger('upload:error', this, event);
                this._destroyListener();
                this.destroy();
            },

            /**
             * Triggered, when upload was aborted
             * @param {XMLHttpRequestProgressEvent} event
             */
            onUploadAbort: function(event) {
                Messenger().error({
                    message: 'An error occurred while transferring the file. Please try again.<br>Reason: ' + event.currentTarget.response,
                });

                this.trigger('upload:abort', this, event);
                this._destroyListener();
                this.destroy();
            },

            /**
             * Attaches correct listeners to the xhr
             * @private
             */
            _bindListener: function() {
                this.onUploadProgress = this.onUploadProgress.bind(this);
                this.onUploadFinished = this.onUploadFinished.bind(this);
                this.onUploadError = this.onUploadError.bind(this);
                this.onUploadAbort = this.onUploadAbort.bind(this);

                if (this.xhr) {
                    this.xhr.upload.addEventListener('progress', this.onUploadProgress, true);
                    this.xhr.addEventListener('load', this.onUploadFinished, true);
                    this.xhr.addEventListener('error', this.onUploadError, true);
                    this.xhr.addEventListener('abort', this.onUploadAbort, true);
                }
            },

            /**
             * Destroys all attached XHR listener
             * @private
             */
            _destroyListener: function() {
                if (this.xhr) {
                    this.xhr.upload.removeEventListener('progress', this.onUploadProgress, true);
                    this.xhr.removeEventListener('load', this.onUploadFinished, true);
                    this.xhr.removeEventListener('error', this.onUploadError, true);
                    this.xhr.removeEventListener('abort', this.onUploadAbort, true);
                    delete this.xhr;
                }
            },
        });
    },

    onRender: function() {
        this.initEvents();
    },

    setCurrentFilesCount: function(n){
        this.settings.currentFiles = n;
    },

    setMaxFilesCount: function(n){
        this.settings.maxFiles = n;
    },

    setSpaceLeft: function(n){
        this.settings.spaceLeft = n;
    },

    /**
     * Remove hidden input
     * @return {void}
     */
    destroyUploadEventHandlers: function() {
        this.$el.off();
        this.hiddenInput.off().remove();
        this.hiddenInputSingle.off().remove();

        _.each(this.fileReader, function(fileReader, idx) {
            if (!_.isUndefined(this.fileReader[idx])){
                this.fileReader[idx].removeEventListener('load', this.fileLoaded, true);
                delete this.fileReader[idx];
            }
        }.bind(this));

        this.fileReader = [];
    },

    /**
     * Initializes the events for the file upload
     */
    initEvents: function() {
        if (!this.eventsInitialized) {
            this.fileLoaded = this.fileLoaded.bind(this);

            this.handleDragEnter = this.handleDragEnter.bind(this);
            this.handleDragLeave = this.handleDragLeave.bind(this);
            this.handleDragOver = this.handleDragOver.bind(this);
            this.handleDrop = this.handleDrop.bind(this);
            this.handleFileSelect = this.handleFileSelect.bind(this);
            this.handleDropboxSelect = this.handleDropboxSelect.bind(this);

            this.hiddenInput = $('<input>').attr({
                type: 'file',
                id: 'uplFileSelect-' + this.cid,
                name: 'files[]',
                multiple: true,
            }).addClass('file-input-multiple').hide();

            this.hiddenInputSingle = $('<input>').attr({
                type: 'file',
                id: 'uplFileSelectSingle-' + this.cid,
                name: 'file',
                capture: 'camera',
            }).addClass('file-input-single').hide();

            if (this.settings.allow.length) {
                this.hiddenInput.attr({
                    accept: this.settings.allow.join(','),
                })
                this.hiddenInputSingle.attr({
                    accept: this.settings.allow.join(','),
                })
            }

            this.$el.append(this.hiddenInput);
            this.$el.append(this.hiddenInputSingle);

            this.$el.on('dragenter', this.handleDragEnter);
            this.$el.on('dragleave', this.handleDragLeave);
            this.$el.on('dragover', this.handleDragOver);
            this.$el.on('drop', this.handleDrop);
            this.hiddenInput.on('change', this.handleFileSelect);
            this.hiddenInputSingle.on('change', this.handleFileSelect);

            this.eventsInitialized = true;
        }
    },

    /**
     * Handler for manual file selection
     * @param {object}  event         The event object
     * @param {object=} eventDelegate Delegated event object, that might be triggered from other dom element.
     */
    handleFileSelect: function(event) {
        var files = event.currentTarget.files;
        this.processFiles(files);
    },

    handleDropboxSelect: function(files) {
        // if (this.areFilesAddable(files) === false) {
        //     return;
        // }
        _.each(files, function(file, index) {
            var model = new this.collection.model({
                importing: true,
                // imageIndex : event.target.index,
                fileUrl: file.link,
                fileName: file.name,
            });

            this.collection.add(model);

            model.importFromUrl();
        }.bind(this));
    },

    /**
     * Handler for a drag enter event
     * @param {object}  event         The event object
     * @param {object=} eventDelegate Delegated event object, that might be triggered from other dom element.
     */
    handleDragEnter: function(event, eventDelegate) {
        event = eventDelegate || event;

        event.stopPropagation();
        event.preventDefault();
        event.originalEvent.dataTransfer.dropEffect = 'copy';
        this.triggerMethod('file:drag:enter', event);
    },

    /**
     * Handler for a drag leave event
     * @param {object}  event         The event object
     * @param {object=} eventDelegate Delegated event object, that might be triggered from other dom element.
     */
    handleDragLeave: function(event, eventDelegate) {
        event = eventDelegate || event;

        event.stopPropagation();
        event.preventDefault();
        this.triggerMethod('file:drag:leave', event);
    },

    /**
     * Handler for a drag over event
     * @param {object}  event         The event object
     * @param {object=} eventDelegate Delegated event object, that might be triggered from other dom element.
     */
    handleDragOver: function(event, eventDelegate) {
        event = eventDelegate || event;

        event.stopPropagation();
        event.preventDefault();
        event.originalEvent.dataTransfer.dropEffect = 'copy';
        this.triggerMethod('file:drag:over', event);
    },

    /**
     * Handler for a drop event
     * @param {Object} event The event object
     */
    handleDrop: function(event, eventDelegate) {
        event = eventDelegate || event;

        event.stopPropagation();
        event.preventDefault();

        // Use items instead of files, to handle directories
        if (event.originalEvent.dataTransfer.items) {
            var items = event.originalEvent.dataTransfer.items;
            for (var i = 0; i < items.length; i++) {
                var entry = items[i];

                if (entry.getAsEntry) {
                    entry = entry.getAsEntry();
                } else if (entry.webkitGetAsEntry) {
                    entry = entry.webkitGetAsEntry();
                }

                this.traverseFileTree(entry);
            }
        } else { // If there is no items array, we use the default files array
            this.processFiles(event.originalEvent.dataTransfer.files);
        }

        this.triggerMethod('file:drag:drop', event);
    },

    /**
     * Method for running through directories
     * @param {object}  item The file item
     * @param {string=} path (Optional) Subpath
     */
    traverseFileTree: function(item, path) {
        path = path || '';

        if (item.isFile) {
            item.file(function(file) {
                this.processFiles([file]);
            }.bind(this));
        } else if (item.isDirectory) {
            var dirReader = item.createReader();
            dirReader.readEntries(function(entries) {
                for (var i = 0; i < entries.length; i++) {
                    this.traverseFileTree(entries[i], path + item.name + '/');
                }
            }.bind(this));
        }
    },

    /**
     * Process the files
     * @param {Object} files The files list
     */
    processFiles: function(files) {
        var internalCount = 0;
        var currentFile;
        var fileReader;

        for (var i = 0; i < Math.min(files.length, this.settings.maxAtOnceFiles); i++) {
            currentFile = files[i];
            fileReader  = new FileReader();

            // Accept file?
            if (this.settings.allow.length === 0 || _.contains(this.settings.allow, currentFile.type)) {
                this.settings.spaceLeft -= currentFile.size;
                this.settings.currentFiles += 1;

                if (this.settings.spaceLeft < 0) {
                    this.settings.spaceLeft += currentFile.size;
                    this.showErrorNoSpaceLeft();

                    return;
                }

                if (this.settings.currentFiles > this.settings.maxFiles) {
                    this.settings.currentFiles -= 1;
                    this.showErrorNoImagesLeft();

                    return;
                }

                // Set attributes so we could use them in callback
                fileReader.index = internalCount;
                fileReader.file  = currentFile;
                internalCount++;

                // Callback for loadEnd Event
                fileReader.addEventListener('load', this.fileLoaded, true);
                fileReader.readAsDataURL(currentFile);

                // Push filereader
                this.fileReader.push(fileReader);
            }
        }
    },

    // areFilesAddable: function(files) {
    //     for (var i = 0; i < Math.min(files.length, this.settings.maxAtOnceFiles); i++) {
    //         if (_.contains(this.settings.allow, currentFile.type)) {
    //             this.settings.spaceLeft -= currentFile.size;
    //             this.settings.currentFiles += 1;

    //             if (this.settings.spaceLeft < 0) {
    //                 this.settings.spaceLeft += currentFile.size;
    //                 this.showErrorNoSpaceLeft();

    //                 return false;
    //             }

    //             if (this.settings.currentFiles > this.settings.maxFiles) {
    //                 this.settings.currentFiles -= 1;
    //                 this.showErrorNoImagesLeft();

    //                 return false;
    //             }

    //             return true;
    //         }
    //     }

    //     return false;
    // },

    /**
     * Shows a restriction modal, when the user hits upload image limit
     */
    showErrorNoImagesLeft: function() {
        if ($('.modal-restriction').length == 0) {
            var restrictionModalView = new RestrictionImagesModalView({hasClose: true});
            var options = {
                additionalClassName: 'modal-center modal-restriction',
                additionalBgClassName: 'light-bg',
            };
            App.addModal(restrictionModalView, options);
        }
    },

    /**
     * Shows a restriction modal, when the user has no mehr diskspace left
     */
    showErrorNoSpaceLeft: function() {
        if ($('.modal-restriction').length == 0) {
            var restrictionModalView = new RestrictionSpaceModalView({hasClose: true});
            var options = {
                additionalClassName: 'modal-center modal-restriction',
                additionalBgClassName: 'light-bg',
            };
            App.addModal(restrictionModalView, options);
        }
    },

    /**
     * Handler for loaded event of a file. We will add the file to the collection and start the upload.
     * @param {Object} event The event object
     */
    fileLoaded: function(event) {
        var model = new this.collection.model({
            uploading: true,
            //imageData: event.target.result, This is hard for large images, so we will use placeholder images for the start
            imageFile: event.target.file,
            imageIndex: event.target.index,
        });

        this.collection.add(model);

        model.upload();
    },
});

module.exports = CompositeUploadView;